import mapboxgl from 'mapbox-gl'
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { ThemeProvider, useTheme } from 'styled-components'
import serial from '~/prix/utils/serial'
import { MapContext } from './mapContext'
import addUpperLayerEventListener from './upperLayerEventListener'
import {
  FeatureCollectionWithOptionalInfoAndStyle,
  FeatureWithInfoAndStyle,
} from '~/packages/sentinel/statsAndGeoToFeatureCollection.util'
import { createPortal } from 'react-dom'

interface GeoJSONProps {
  geoJson: FeatureCollectionWithOptionalInfoAndStyle
  customCirclePaint?: mapboxgl.CirclePaint
  customLinePaint?: mapboxgl.LinePaint
  customPolygonPaint?: mapboxgl.FillPaint
  renderInfoWindow?: (props: { feature: FeatureWithInfoAndStyle }) => JSX.Element
  onClick?: (
    event: mapboxgl.MapMouseEvent & mapboxgl.EventData,
    feature: FeatureWithInfoAndStyle,
  ) => void
}

const ids = serial()

const GeoJSON = ({ geoJson, renderInfoWindow, onClick }: GeoJSONProps) => {
  const mapContext = useContext(MapContext)
  const theme = useTheme()

  if (!mapContext) {
    throw new Error('GeoJSON must be used within a Map')
  }

  const { map } = mapContext

  //indica que o render inicial do componente foi executado
  const initRender = useRef(false)
  const id = useRef(ids.next())
  const idSuffix = useMemo(() => `-geojson-${id.current}`, [id])
  const sourceId = useMemo(() => `source${idSuffix}`, [id])

  const [currentPopup, setCurrentPopup] = useState<{
    id: string
    node: HTMLElement
    feature: FeatureWithInfoAndStyle
  } | null>(null)

  useEffect(() => {
    if (geoJson.features.length === 0 || initRender.current) return

    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    map.addSource(sourceId, {
      type: 'geojson',
      data: geoJson as GeoJSON.FeatureCollection<GeoJSON.Geometry>,
    })

    const beforeLayerId = 'road-label'

    map.addLayer(
      {
        id: 'polygons' + idSuffix,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': ['get', 'fillOpacity'],
        },
        filter: ['==', '$type', 'Polygon'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'polygon-borders' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'strokeColor'],
          'line-width': ['get', 'strokeWeight'],
          'line-opacity': ['get', 'strokeOpacity'],
        },
        filter: ['==', '$type', 'Polygon'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'lines-hitbox' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': 'rgba(0, 0, 0, 0)',
          'line-width': 12,
        },
        filter: ['==', '$type', 'LineString'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'lines' + idSuffix,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'strokeColor'],
          'line-width': ['get', 'strokeWeight'],
          'line-opacity': ['get', 'strokeOpacity'],
        },
        filter: ['==', '$type', 'LineString'],
      },
      beforeLayerId,
    )

    map.addLayer(
      {
        id: 'points' + idSuffix,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': ['get', 'fillColor'],
        },
        filter: ['==', '$type', 'Point'],
      },
      beforeLayerId,
    )

    //marca que renderizou a primeira vez
    initRender.current = true
  }, [map, geoJson, initRender])

  useEffect(() => {
    return () => {
      try {
        if (!map || !map.getStyle() || !map.getSource) {
          return
        }
      } catch {
        return
      }

      if (map.getLayer('points' + idSuffix)) map.removeLayer('points' + idSuffix)
      if (map.getLayer('lines' + idSuffix)) map.removeLayer('lines' + idSuffix)
      if (map.getLayer('lines-hitbox' + idSuffix)) map.removeLayer('lines-hitbox' + idSuffix)
      if (map.getLayer('polygons' + idSuffix)) map.removeLayer('polygons' + idSuffix)
      if (map.getLayer('polygon-borders' + idSuffix)) map.removeLayer('polygon-borders' + idSuffix)
      if (map.getSource(sourceId)) {
        try {
          map.removeSource(sourceId)
        } catch (error) {
          console.error('Error executing removeSource function from mapbox: ', error)
        }
      }
    }
  }, [map])

  const popupRef = useRef<mapboxgl.Popup>()

  useEffect(() => {
    try {
      if (!initRender.current || !map || !map.getStyle() || !map.getSource) {
        return
      }
    } catch {
      return
    }

    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
    if (!source) return

    source.setData(geoJson as GeoJSON.FeatureCollection<GeoJSON.Geometry>)

    const layers = [
      'points' + idSuffix,
      'lines' + idSuffix,
      'lines-hitbox' + idSuffix,
      'polygons' + idSuffix,
      'polygon-borders' + idSuffix,
    ]

    if (!popupRef.current) {
      popupRef.current = new mapboxgl.Popup()
    }
    const popup = popupRef.current

    const handleClick = (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      const features = map.queryRenderedFeatures(event.point, { layers })
      if (!features.length) return

      const rawFeature = features[0] as GeoJSON.Feature
      if ('toJSON' in rawFeature && typeof rawFeature.toJSON === 'function') {
        const feature = rawFeature.toJSON() as FeatureWithInfoAndStyle
        const featureWithPositionClicked = {
          ...feature,
          properties: { ...feature.properties, positionClicked: event.lngLat },
        }
        if (renderInfoWindow) {
          const div = document.createElement('div')
          div.className = 'prix-info-window'

          setCurrentPopup({
            id: Date.now().toString(),
            node: div,
            feature: featureWithPositionClicked,
          })

          popup.setLngLat(event.lngLat)
          popup.setDOMContent(div)
          popup.setMaxWidth('none')
          popup.addTo(map)

          popup.once('close', () => {
            setCurrentPopup(null)
          })
        }

        if (onClick) {
          onClick(event, feature)
        }
      }
    }

    const offClick = addUpperLayerEventListener(map, 'click', layers, handleClick)

    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      popup.remove()
      setCurrentPopup(null)
      offClick()
    }
  }, [map, geoJson, initRender, onClick, renderInfoWindow, theme])

  return (
    currentPopup &&
    renderInfoWindow &&
    createPortal(renderInfoWindow({ feature: currentPopup.feature }), currentPopup.node)
  )
}

export default memo(GeoJSON)
